/**
 * Auto-reply (owner-only): track a hashtag or keyword on one connected
 * account, and either see it queued automatically or review the model's
 * draft before it goes out.
 *
 * There is no capability gate for this panel — it renders only in the owner
 * console (`Console.tsx` passes `scope`), because the feature itself is
 * owner-run bots only (no rental path yet). The server enforces the same
 * thing independently; this is not the authorization boundary, just where it
 * is not offered.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { EMPTY_STATES } from '@pm/shared';
import { apiFetch } from '../../api/client';
import { toProblem, type Problem } from '../../api/problem';
import { useApi } from '../../hooks/useApi';
import type { Bot, ListeningRule, ListeningMatch, Page } from '../../api/types';
import { Card, Field, Loading, Badge, type Tone } from '../../components/ui';
import { ConfirmButton } from '../../components/ConfirmButton';
import { EmptyState } from '../../components/EmptyState';
import { ErrorView } from '../../components/ErrorView';
import { localDateTime, percent } from '../../lib/format';
import { routes } from '../../api/routes';

function statusTone(status: ListeningMatch['status']): Tone {
  switch (status) {
    case 'auto_sent':
    case 'sent':
      return 'ok';
    case 'needs_review':
      return 'warn';
    case 'score_failed':
      return 'bad';
    case 'dismissed':
      return 'neutral';
    default:
      return 'info'; // pending
  }
}

function statusLabel(status: ListeningMatch['status']): string {
  switch (status) {
    case 'auto_sent':
      return 'auto-sent';
    case 'needs_review':
      return 'needs review';
    case 'score_failed':
      return "couldn't score";
    default:
      return status;
  }
}

/** Mirrors the RATES table in services/generation/anthropic.ts. Kept in sync by hand — the two never drift far apart. */
const KNOWN_MODELS = [
  { id: '', label: 'Platform default' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5 — fastest, cheapest' },
  { id: 'claude-sonnet-5', label: 'Sonnet 5' },
  { id: 'claude-opus-5', label: 'Opus 5' },
];

function RuleForm({ bot, onCreated }: { bot: Bot; onCreated: () => void }) {
  const accounts = bot.social_accounts;
  const [socialAccountId, setSocialAccountId] = useState(accounts[0]?.id ?? '');
  const [q, setQ] = useState('');
  const [threshold, setThreshold] = useState(0.8);
  const [dailyCap, setDailyCap] = useState(20);
  const [replyCriteria, setReplyCriteria] = useState('');
  const [replyMode, setReplyMode] = useState<'auto' | 'approve'>('approve');
  const [model, setModel] = useState('');
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  if (accounts.length === 0) {
    return <p className="muted">Connect an account before tracking a hashtag or keyword.</p>;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!socialAccountId || !q.trim()) return;
    setPending(true);
    setProblem(null);
    try {
      await apiFetch(routes.listening.rules(bot.id), {
        method: 'POST',
        body: {
          social_account_id: socialAccountId,
          query: q.trim(),
          confidence_threshold: threshold,
          daily_reply_cap: dailyCap,
          reply_mode: replyMode,
          ...(replyCriteria.trim() ? { reply_criteria: replyCriteria.trim() } : {}),
          ...(model ? { model } : {}),
        },
      });
      setQ('');
      setReplyCriteria('');
      onCreated();
    } catch (err) {
      setProblem(toProblem(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="listening-rule-form">
      {problem ? <ErrorView problem={problem} /> : null}
      <Field id="lr-account" label="Account">
        <select
          id="lr-account"
          className="input"
          value={socialAccountId}
          onChange={(e) => setSocialAccountId(e.target.value)}
        >
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.platform} @{a.external_handle}
            </option>
          ))}
        </select>
      </Field>
      <Field id="lr-query" label="Hashtag or keyword" hint="Passed straight to that platform's search.">
        <input
          id="lr-query"
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="#launch"
        />
      </Field>
      <Field id="lr-threshold" label={`Auto-send at or above ${percent(threshold, 0)} confidence`}>
        <input
          id="lr-threshold"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
        />
      </Field>
      <Field id="lr-cap" label="Max auto-sends per day" hint="A match over the cap still gets drafted for review.">
        <input
          id="lr-cap"
          type="number"
          min={0}
          max={500}
          className="input"
          value={dailyCap}
          onChange={(e) => setDailyCap(Number(e.target.value))}
        />
      </Field>
      <Field
        id="lr-criteria"
        label="Reply criteria"
        hint="Extra scoring instructions beyond persona voice/topic fit, e.g. &quot;only reply to people looking to purchase, not people advertising their own.&quot;"
      >
        <textarea
          id="lr-criteria"
          className="input"
          rows={2}
          maxLength={500}
          value={replyCriteria}
          onChange={(e) => setReplyCriteria(e.target.value)}
          placeholder="Optional"
        />
      </Field>
      <Field id="lr-mode" label="Reply mode">
        <select
          id="lr-mode"
          className="input"
          value={replyMode}
          onChange={(e) => setReplyMode(e.target.value as 'auto' | 'approve')}
        >
          <option value="approve">Draft for my approval</option>
          <option value="auto">Send automatically above threshold</option>
        </select>
      </Field>
      <Field id="lr-model" label="Model">
        <select id="lr-model" className="input" value={model} onChange={(e) => setModel(e.target.value)}>
          {KNOWN_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </Field>
      <button type="submit" className="btn btn-primary" disabled={pending || !q.trim()}>
        {pending ? 'Adding…' : 'Add rule'}
      </button>
    </form>
  );
}

function RuleRow({ rule, onChanged }: { rule: ListeningRule; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const toggle = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await apiFetch(routes.listening.rule(rule.id), { method: 'PATCH', body: { enabled: !rule.enabled } });
      onChanged();
    } catch (err) {
      setProblem(toProblem(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await apiFetch(routes.listening.rule(rule.id), { method: 'DELETE' });
      onChanged();
    } catch (err) {
      setProblem(toProblem(err));
      setBusy(false);
    }
  };

  return (
    <li className="listening-rule-item">
      <div className="listening-rule-main">
        <Badge tone={rule.enabled ? 'ok' : 'neutral'}>{rule.enabled ? 'on' : 'off'}</Badge>
        <Badge tone={rule.reply_mode === 'auto' ? 'warn' : 'info'}>
          {rule.reply_mode === 'auto' ? 'auto-send' : 'draft for approval'}
        </Badge>
        <strong>{rule.query}</strong>
        <span className="muted">
          {rule.platform} @{rule.external_handle}
          {rule.reply_mode === 'auto'
            ? ` · auto-send ≥ ${percent(rule.confidence_threshold, 0)} · up to ${rule.daily_reply_cap}/day`
            : null}
          {rule.model ? ` · ${rule.model}` : null}
        </span>
        {rule.reply_criteria ? <p className="muted listening-rule-criteria">Criteria: {rule.reply_criteria}</p> : null}
        {problem ? <ErrorView problem={problem} /> : null}
      </div>
      <div className="listening-rule-actions">
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void toggle()}>
          {rule.enabled ? 'Pause' : 'Resume'}
        </button>
        <ConfirmButton
          label="Delete"
          confirmTitle="Delete this rule?"
          confirmBody={`Matches already found for "${rule.query}" stay on record; nothing new will be found once it's gone.`}
          confirmLabel="Delete"
          pending={busy}
          onConfirm={() => void remove()}
        />
      </div>
    </li>
  );
}

function MatchCard({ match, onChanged }: { match: ListeningMatch; onChanged: () => void }) {
  const [draft, setDraft] = useState(match.suggested_reply ?? '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await apiFetch(routes.listening.matchSend(match.id), { method: 'POST', body: { body: draft } });
      onChanged();
    } catch (err) {
      setProblem(toProblem(err));
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await apiFetch(routes.listening.matchDismiss(match.id), { method: 'POST' });
      onChanged();
    } catch (err) {
      setProblem(toProblem(err));
      setBusy(false);
    }
  };

  return (
    <li className="listening-match-item">
      <div className="listening-match-main">
        <Badge tone={statusTone(match.status)}>{statusLabel(match.status)}</Badge>
        {match.confidence !== null ? <Badge tone="info">{percent(match.confidence, 0)} confidence</Badge> : null}
        <p className="muted">
          {match.query} · @{match.author_handle} · {localDateTime(match.created_at)}
        </p>
        <p className="listening-match-body">{match.body}</p>
      </div>

      {problem ? <ErrorView problem={problem} /> : null}

      {match.status === 'needs_review' ? (
        <div className="listening-match-draft">
          <textarea
            className="input"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="The model did not draft a reply — write one to send."
          />
          <div className="listening-match-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || !draft.trim()}
              onClick={() => void send()}
            >
              Send
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void dismiss()}>
              Dismiss
            </button>
          </div>
        </div>
      ) : match.suggested_reply ? (
        <p className="listening-match-reply">“{match.suggested_reply}”</p>
      ) : null}
    </li>
  );
}

export function AutoReply({ bot }: { bot: Bot }) {
  const rules = useApi<Page<ListeningRule>>(routes.listening.rules(bot.id));
  const matches = useApi<Page<ListeningMatch>>(routes.listening.matches(bot.id), {
    query: { limit: 50 },
    pollMs: 30_000,
  });

  const reloadAll = () => {
    rules.reload();
    matches.reload();
  };

  return (
    <div className="listening">
      <Card title="Auto-reply rules">
        <RuleForm bot={bot} onCreated={reloadAll} />
        {rules.initialLoading ? <Loading label="Loading rules" /> : null}
        {rules.problem ? <ErrorView problem={rules.problem} onRetry={rules.reload} /> : null}
        {rules.data && rules.data.data.length === 0 ? (
          <EmptyState heading={EMPTY_STATES.listeningNoRules.heading} body={EMPTY_STATES.listeningNoRules.body} />
        ) : null}
        {rules.data && rules.data.data.length > 0 ? (
          <ul className="listening-rule-list">
            {rules.data.data.map((r) => (
              <RuleRow key={r.id} rule={r} onChanged={reloadAll} />
            ))}
          </ul>
        ) : null}
      </Card>

      <Card title="Matches">
        {matches.initialLoading ? <Loading label="Loading matches" /> : null}
        {matches.problem ? <ErrorView problem={matches.problem} onRetry={matches.reload} /> : null}
        {matches.data && matches.data.data.length === 0 ? (
          <EmptyState
            heading={EMPTY_STATES.listeningNoMatches.heading}
            body={EMPTY_STATES.listeningNoMatches.body}
          />
        ) : null}
        {matches.data && matches.data.data.length > 0 ? (
          <ul className="listening-match-list">
            {matches.data.data.map((m) => (
              <MatchCard key={m.id} match={m} onChanged={reloadAll} />
            ))}
          </ul>
        ) : null}
      </Card>
    </div>
  );
}
